import gql from 'graphql-tag';
import { useQuery } from '@vue/apollo-composable';
import { reactive, ref, watch } from 'vue';
import useDecryptSymbols from './useDecryptSymbols';
import useCurrentUser from './useCurrentUser';

const { decryptSymbols } = useDecryptSymbols();
const { user } = useCurrentUser();

const quotes = reactive([]);
const loadingQuotes = ref(false);
const refetchQuotes = ref(null);

const ALL_STATUSES = ['open', 'rejected', 'converted'];

const filters = reactive({
  salespersonId: null,
  statuses: ['open', 'converted'],
  dateCreatedFrom: null,
  dateCreatedTo: null,
  dateRequiredFrom: null,
  dateRequiredTo: null,
});

const searchText = ref('');

const GET_QUOTES = gql`
  query QuotesQuery($salespersonId: String, $statuses: [String], $dateCreatedFrom: String, $dateCreatedTo: String, $dateRequiredFrom: String, $dateRequiredTo: String) {
    quotes(salespersonId: $salespersonId, statuses: $statuses, dateCreatedFrom: $dateCreatedFrom, dateCreatedTo: $dateCreatedTo, dateRequiredFrom: $dateRequiredFrom, dateRequiredTo: $dateRequiredTo) {
      QUOTEID
      QUOTENUM
      COMPANYID
      CONTACTID
      ASSIGNEDID
      JOBNAME
      DATEREQUIRED
      CREATEDATE
      VALIDTO
      STATUS
      NOTES
      DISCOUNTPCT
      items {
        LINETYPE
        QTY
        UNITPRICE
        DISCOUNTPCT
      }
      company {
        COMPANYID
        NAME
        phones {
          NUMBERDISPLAY
          TYPEID
        }
      }
      contact {
        CONTACTID
        FIRSTNAME
        LASTNAME
        phones {
          NUMBERDISPLAY
          TYPEID
        }
      }
      salesperson {
        USERID
        DISPLAYNAME
      }
    }
  }
`;

function setSalesperson() {
  if (user.userid) {
    filters.salespersonId = user.userid;
  }
}

setSalesperson();

watch(
  user,
  () => {
    if (!filters.salespersonId) setSalesperson();
  },
  { deep: true }
);

function getQuoteQueryVariables() {
  const isSearching = searchText.value.trim().length > 0;

  if (isSearching) {
    return {
      salespersonId: null,
      statuses: ALL_STATUSES,
      dateCreatedFrom: null,
      dateCreatedTo: null,
      dateRequiredFrom: null,
      dateRequiredTo: null,
    };
  }

  return {
    salespersonId: filters.salespersonId,
    statuses: filters.statuses,
    dateCreatedFrom: filters.dateCreatedFrom,
    dateCreatedTo: filters.dateCreatedTo,
    dateRequiredFrom: filters.dateRequiredFrom,
    dateRequiredTo: filters.dateRequiredTo,
  };
}

function useQuotesQuery() {
  const { result, loading, error, refetch } = useQuery(GET_QUOTES, getQuoteQueryVariables(), { fetchPolicy: 'no-cache' });
  refetchQuotes.value = refetch;
  loadingQuotes.value = loading.value;

  watch(result, () => {
    const decrypted = decryptSymbols(result.value?.quotes || []);
    quotes.splice(0, quotes.length, ...decrypted);
  });

  watch(
    () => searchText.value.trim().length > 0,
    () => {
      refetch(getQuoteQueryVariables());
    }
  );

  watch(filters, () => {
    if (!searchText.value.trim()) {
      refetch(getQuoteQueryVariables());
    }
  }, { deep: true });

  watch(loading, () => {
    loadingQuotes.value = loading.value;
  });

  return { result, loading, error };
}

export default {
  useQuotesQuery,
  filters,
  searchText,
  quotes,
  loadingQuotes,
  refetchQuotes,
};
